// Block ids (`blockId`): the one place they are defined.
//
// The canonical markdown never contains ids. A block id exists only as a node
// attribute of the "document" schema, on the blocks directly under the doc. The
// generator lives here too so the screen and the server never disagree.

import { Extension } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { nanoid } from "nanoid";

/** The node attribute. */
export const BLOCK_ID_ATTR = "blockId";

/** The DOM attribute (selectors of block UI use it). */
export const BLOCK_ID_DOM_ATTR = "data-block-id";

/**
 * Node types that may carry a block id: the blocks that can appear directly
 * under the doc (depth 0). The same type nested deeper (a paragraph in a list
 * item) keeps a null id; normalizing that is the job of the plugin and the
 * server. `detailsSummary` / `detailsContent` / `calloutTitle` /
 * `calloutContent` only appear inside their parent and are not listed.
 */
export const BLOCK_ID_TYPES: readonly string[] = [
  "paragraph",
  "heading",
  "codeBlock",
  "blockquote",
  "bulletList",
  "orderedList",
  "horizontalRule",
  "table",
  "taskList",
  "trameriaFence",
  "trameriaFile",
  "opaqueBlock",
  "footnoteDef",
  "details",
  "callout",
];

/**
 * A new block id: `b` + 10 nanoid characters (`[A-Za-z0-9_-]`, URL safe).
 * Unique within a page is enough.
 */
export function generateBlockId(): string {
  return `b${nanoid(10)}`;
}

/**
 * Whether a top-level node should get an id: its type declares `blockId` in the
 * schema (the attrs have the key, so a schema without block ids never grows
 * one) and it has none yet. Callers pass only depth-0 children.
 */
export function isSeedableBlockNode(node: {
  attrs?: Record<string, unknown>;
}): boolean {
  const attrs = node.attrs ?? {};
  if (!Object.hasOwn(attrs, BLOCK_ID_ATTR)) return false;
  const current = attrs[BLOCK_ID_ATTR];
  return !(typeof current === "string" && current.length > 0);
}

/**
 * Gives ids to the top-level blocks that have none. Existing ids are kept
 * (idempotent); nested nodes are never touched. Returns the same instance when
 * nothing changed, so callers can tell by identity.
 */
export function seedBlockIds(doc: PMNode): PMNode {
  const children: PMNode[] = [];
  let changed = false;
  doc.forEach((child) => {
    if (!isSeedableBlockNode(child)) {
      children.push(child);
      return;
    }
    children.push(
      child.type.create(
        { ...child.attrs, [BLOCK_ID_ATTR]: generateBlockId() },
        child.content,
        child.marks,
      ),
    );
    changed = true;
  });
  return changed ? doc.type.create(doc.attrs, children, doc.marks) : doc;
}

/**
 * `data-block-id` for nodes whose own `renderHTML` drops the merged
 * `HTMLAttributes` (so the global attribute is not added automatically). No
 * attribute at all when there is no id, so an empty value never matches a selector.
 */
export function blockIdDomAttrs(node: {
  attrs?: Record<string, unknown>;
}): Record<string, string> {
  const blockId = node.attrs?.[BLOCK_ID_ATTR];
  return typeof blockId === "string" && blockId.length > 0
    ? { [BLOCK_ID_DOM_ATTR]: blockId }
    : {};
}

/**
 * Declares `blockId` on every type in {@link BLOCK_ID_TYPES} (global
 * attributes). Only in the "document" profile. Never written to markdown: the
 * markdown renderers read only the attributes they know.
 */
export const BlockIdAttributes = Extension.create({
  name: "blockId",

  addGlobalAttributes() {
    return [
      {
        types: [...BLOCK_ID_TYPES],
        attributes: {
          [BLOCK_ID_ATTR]: {
            default: null,
            parseHTML: (element) => element.getAttribute(BLOCK_ID_DOM_ATTR),
            renderHTML: (attributes) =>
              attributes[BLOCK_ID_ATTR]
                ? { [BLOCK_ID_DOM_ATTR]: attributes[BLOCK_ID_ATTR] }
                : {},
          },
        },
      },
    ];
  },
});
